import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance for session-based requests
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 seconds for general requests
});

// Create specific instance for analysis requests with longer timeout
const analysisApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for GPT-4.1 deep reasoning analysis
});

// Response interceptor for error handling
const responseInterceptor = (response: any) => response;
const errorInterceptor = (error: any) => {
  if (error.response?.status === 404 && error.response?.data?.error?.includes('Session not found')) {
    // Session expired or invalid
    window.location.href = '/';
  }
  return Promise.reject(error);
};

api.interceptors.response.use(responseInterceptor, errorInterceptor);
analysisApiClient.interceptors.response.use(responseInterceptor, errorInterceptor);

export interface SessionData {
  id: string;
  sessionKey: string;
  clientName?: string;
  counterParty?: string;
  casePosition?: 'PLAINTIFF' | 'DEFENDANT' | 'INTERVENOR';
  status: 'ACTIVE' | 'ARCHIVED' | 'DELETED';
  accessCount: number;
  lastAccessed: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  documents?: any[];
  analyses?: any[];
  _count?: {
    documents: number;
    analyses: number;
    chatMessages: number;
  };
}

export interface CreateSessionRequest {
  clientName?: string;
  counterParty?: string;
  casePosition?: 'PLAINTIFF' | 'DEFENDANT' | 'INTERVENOR';
}

export interface SessionStats {
  documents: {
    total: number;
    processed: number;
    processing: number;
  };
  analyses: {
    total: number;
    byType: Record<string, number>;
  };
  chatMessages: number;
  accessCount: number;
  lastAccessed: string;
  createdAt: string;
  expiresAt?: string;
}

export const sessionApi = {
  // Create new session
  create: async (sessionData: CreateSessionRequest): Promise<{ sessionKey: string; session: SessionData }> => {
    const response = await api.post('/sessions', sessionData);
    return response.data;
  },

  // Get session by key
  getByKey: async (sessionKey: string): Promise<SessionData> => {
    const response = await api.get(`/sessions/${sessionKey}`);
    return response.data;
  },

  // Update session
  update: async (sessionKey: string, sessionData: Partial<CreateSessionRequest & { status?: string }>): Promise<SessionData> => {
    const response = await api.put(`/sessions/${sessionKey}`, sessionData);
    return response.data;
  },

  // Get session statistics
  getStats: async (sessionKey: string): Promise<SessionStats> => {
    const response = await api.get(`/sessions/${sessionKey}/stats`);
    return response.data;
  },

  // Extend session expiration
  extendSession: async (sessionKey: string, days: number = 30): Promise<{ message: string; expiresAt: string }> => {
    const response = await api.post(`/sessions/${sessionKey}/extend`, { days });
    return response.data;
  },

  // Get shareable URL
  getShareUrl: async (sessionKey: string): Promise<{ shareUrl: string; sessionKey: string; expiresAt?: string }> => {
    const response = await api.get(`/sessions/${sessionKey}/share`);
    return response.data;
  }
};

export const documentsApi = {
  // Upload documents
  upload: async (sessionKey: string, files: FileList): Promise<{ documents: any[]; errors?: any[] }> => {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append('documents', file);
    });

    const response = await api.post(`/documents/upload/${sessionKey}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get session documents
  getBySession: async (sessionKey: string, params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ documents: any[]; total: number; hasMore: boolean }> => {
    const response = await api.get(`/documents/session/${sessionKey}`, { params });
    return {
      documents: response.data.documents,
      total: response.data.total,
      hasMore: response.data.hasMore
    };
  },

  // Get document by ID
  getById: async (documentId: string, sessionKey: string): Promise<any> => {
    const response = await api.get(`/documents/${documentId}`, {
      params: { sessionKey }
    });
    return response.data;
  },

  // Get document content
  getContent: async (documentId: string, sessionKey: string): Promise<{ content: string; wordCount: number }> => {
    const response = await api.get(`/documents/${documentId}/content`, {
      params: { sessionKey }
    });
    return response.data;
  },

  // Search document
  search: async (documentId: string, query: string, sessionKey: string, caseSensitive = false): Promise<any> => {
    const response = await api.post(`/documents/${documentId}/search`, {
      query,
      caseSensitive,
      sessionKey
    });
    return response.data;
  },

  // Delete document
  delete: async (documentId: string, sessionKey: string): Promise<void> => {
    await api.delete(`/documents/${documentId}`, {
      params: { sessionKey }
    });
  }
};

export const analysisApi = {
  // Get analysis types
  getTypes: async (): Promise<Record<string, any[]>> => {
    const response = await api.get('/analysis/types');
    return response.data;
  },

  // Generate analysis
  generate: async (sessionKey: string, analysisType: string, documentIds?: string[]): Promise<any> => {
    const response = await analysisApiClient.post('/analysis/generate', {
      sessionKey,
      analysisType,
      documentIds
    });
    return response.data;
  },

  // Get session analyses
  getBySession: async (sessionKey: string, params?: {
    type?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ analyses: any[]; total: number; hasMore: boolean }> => {
    const response = await api.get(`/analysis/session/${sessionKey}`, { params });
    return {
      analyses: response.data.analyses,
      total: response.data.total,
      hasMore: response.data.hasMore
    };
  },

  // Get analysis by ID
  getById: async (analysisId: string, sessionKey: string): Promise<any> => {
    const response = await api.get(`/analysis/${analysisId}`, {
      params: { sessionKey }
    });
    return response.data;
  },

  // Delete analysis
  delete: async (analysisId: string, sessionKey: string): Promise<void> => {
    await api.delete(`/analysis/${analysisId}`, {
      params: { sessionKey }
    });
  }
};

export const chatApi = {
  // Send message
  sendMessage: async (sessionKey: string, message: string): Promise<{ response: string; timestamp: string }> => {
    const response = await api.post('/chat', { sessionKey, message });
    return response.data;
  },

  // Get chat history
  getHistory: async (sessionKey: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ messages: any[]; total: number; hasMore: boolean }> => {
    const response = await api.get(`/chat/${sessionKey}/history`, { params });
    return {
      messages: response.data.messages,
      total: response.data.total,
      hasMore: response.data.total > (params?.offset || 0) + response.data.messages.length
    };
  },

  // Clear history
  clearHistory: async (sessionKey: string): Promise<void> => {
    await api.delete(`/chat/${sessionKey}/history`);
  }
};

export default api;